import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { PixelService } from '../../../../core/services/pixel.service';
// import { Step0Platforms } from './Step0Platforms';
import { Step1GeneratePixel } from './Step1GeneratePixel';
import { Step2AddToCart } from './Step2AddToCart';
import { Step3Checkout } from './Step3Checkout';
import { Step4Purchase } from './Step4Purchase';
import CustomStepper from '../components/CustomStepper';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import useBodyClass from '../../../../hooks/useBodyClass';

const STEPS = ['Generate Pixel', 'Add to Cart', 'Initiate Checkout', 'Track Purchase'];
// const STEPS = ['Choose Platforms', 'Generate Pixel', 'Add to Cart', 'Initiate Checkout', 'Track Purchase'];
// Platform chooser (Step0Platforms) removed — every step now shows ALL integration
// types as tabs (Website | Mobile App SDK | Server API), like Stripe/Meta docs.
// Asking upfront filtered the snippets and locked completed brands out of the
// other options. cct_pixels.platforms stays as optional metadata only.
const SUMMARY_STEP = 4;

// // cct_pixels.platforms arrives as JSON string or array depending on driver
// const parsePlatforms = (raw: any): string[] => {
//     try {
//         if (Array.isArray(raw)) return raw;
//         if (typeof raw === 'string' && raw) return JSON.parse(raw);
//     } catch {
//         // fall through
//     }
//     return [];
// };

interface Props {
    UserReducer: any;
    UserID: any;
}

const BrandAffiliateOnboardingComponent: React.FC<Props> = ({ UserReducer }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [pixelData, setPixelData] = useState<any>(null);
    // const [platforms, setPlatforms] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    const isComplete = pixelData?.status === 'active';
    useBodyClass('brand-affiliate-onboarding');
    useEffect(() => {
        loadPixelStatus();
    }, []);

    const loadPixelStatus = async () => {
        try {
            const res = await PixelService.getPixelStatus(
                UserReducer.influencer_id,
                UserReducer.account_id
            );
            if (res?.data?.rows?.length > 0) {
                const row = res.data.rows[0];
                setPixelData(row);
                // setPlatforms(parsePlatforms(row.platforms));
                const step = (row.onboarding_step || 1) - 1;
                setActiveStep(row.status === 'active' ? SUMMARY_STEP : Math.min(step, 3));
            }
        } catch {
            // No pixel yet — start fresh at step 0
        } finally {
            setLoading(false);
        }
    };

    // Platform chooser removed — kept for reference if a per-platform filter returns.
    // const handlePlatformsContinue = async (selected: string[]) => {
    //     setPlatforms(selected);
    //     setActiveStep(1);
    //     if (pixelData) {
    //         await PixelService.updatePlatforms({
    //             influencer_id: UserReducer.influencer_id,
    //             account_id: UserReducer.account_id,
    //             platforms: selected,
    //         });
    //         setPixelData((prev: any) => ({ ...prev, platforms: JSON.stringify(selected) }));
    //     }
    // };

    const handlePixelGenerated = async (data: any) => {
        setPixelData(data);
        // Step 0→1: pixel just created, save step 2 to DB then advance
        setActiveStep(1);
        await PixelService.updateStep({
            influencer_id: UserReducer.influencer_id,
            account_id: UserReducer.account_id,
            onboarding_step: 2,
        });
    };

    const advanceTo = async (step: number) => {
        const nextStep = Math.min(step, 3);
        setActiveStep(nextStep);
        if (nextStep > 0 && nextStep < 3) {
            await PixelService.updateStep({
                influencer_id: UserReducer.influencer_id,
                account_id: UserReducer.account_id,
                onboarding_step: nextStep + 1,
            });
        }
    };

    const handleComplete = async () => {
        await PixelService.activatePixel({
            influencer_id: UserReducer.influencer_id,
            account_id: UserReducer.account_id,
        });
        setPixelData((prev: any) => ({ ...prev, status: 'active', onboarding_step: 4 }));
        // setActiveStep(4);
        setActiveStep(SUMMARY_STEP);
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    const renderStep = () => {
        switch (activeStep) {
            // Step0Platforms removed — all integration types are always shown as tabs
            case 0:
                return (
                    <Step1GeneratePixel
                        UserReducer={UserReducer}
                        existingPixel={pixelData}
                        isReadOnly={isComplete}
                        onGenerated={handlePixelGenerated}
                    />
                );

            case 1:
                return (
                    <Step2AddToCart
                        cctm_id={pixelData?.cctm_id}
                        pixel={pixelData}
                        isReadOnly={isComplete}
                        onNext={() => advanceTo(2)}
                        onBack={() => setActiveStep(0)}
                    />
                );

            case 2:
                return (
                    <Step3Checkout
                        cctm_id={pixelData?.cctm_id}
                        pixel={pixelData}
                        isReadOnly={isComplete}
                        onNext={() => advanceTo(3)}
                        onBack={() => setActiveStep(1)}
                    />
                );

            case 3:
                return (
                    <Step4Purchase
                        cctm_id={pixelData?.cctm_id}
                        pixel={pixelData}
                        isComplete={isComplete}
                        onComplete={handleComplete}
                        onBack={() => setActiveStep(2)}
                    />
                );

            case SUMMARY_STEP:
                return (
                    <Box textAlign="center" py={6}>
                        <TaskAltIcon
                            sx={{
                                fontSize: 100,
                                color: '#22c55e',
                                mb: 2,
                            }}
                        />

                        <Typography
                            variant="h4"
                            fontWeight={800}
                            mb={2}
                        >
                            🎉 Affiliate Tracking Active
                        </Typography>

                        <Typography
                            color="text.secondary"
                            maxWidth={600}
                            mx="auto"
                        >
                            {/* Your website is now tracking pageviews,
                            add-to-cart events, checkout events,
                            and completed purchases. */}
                            ClanConnect is now tracking pageviews,
                            add-to-cart events, checkout events, and completed
                            purchases — from your website, mobile app, or backend.
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };
    return (
        <Box
            sx={{
                py: 8,
                // maxWidth: '900px',
                // Wider on desktop — the code + guide two-column layout needs room
                maxWidth: { xs: '100%', md: '1080px', lg: '1280px' },
                margin: '0 auto',
                padding: { xs: 3, md: 4 },
                borderRadius: '14px',
                background: `
                radial-gradient(circle at top left,#DBEAFE 0%,transparent 25%),
                radial-gradient(circle at top right,#EDE9FE 0%,transparent 25%),
                #F8FAFC
            `,
            }}
        >
            <Box textAlign="center" mb={6} sx={{maxWidth:'900px', mx:'auto'}}>
                <Typography
                    sx={{
                        color: '#2563eb',
                        fontWeight: 700,
                        letterSpacing: 2,
                        mb: 1,
                    }}
                >
                    Setup Your Pixel
                </Typography>

                <Typography
                    variant="h4"
                    fontWeight={800}
                    mb={1}
                >
                    Affiliate Pixel Setup
                </Typography>

                <Typography color="text.secondary">
                    Install the ClanConnect pixel on your website to track affiliate-driven sales.
                </Typography>
            </Box>
            <CustomStepper
                steps={STEPS}
                // activeStep={isComplete ? 4 : activeStep}
                activeStep={isComplete ? SUMMARY_STEP : activeStep}
                onStepClick={(step) => {
                    if (isComplete) {
                        setActiveStep(step);
                    }
                }}
            />

            {/* <Stepper activeStep={isComplete ? 4 : activeStep} alternativeLabel sx={{ mb: 5 }}>
                {STEPS.map((label, index) => (
                    <Step key={label} completed={isComplete || index < activeStep}>
                        <StepLabel
                            onClick={isComplete ? () => setActiveStep(index) : undefined}
                            sx={isComplete ? { cursor: 'pointer' } : undefined}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper> */}


            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    p: { xs: 3, md: 3 },
                    backdropFilter: 'blur(12px)',
                    background: 'rgba(255,255,255,.85)',
                    border: '1px solid rgba(255,255,255,.6)',
                    boxShadow:
                        '0 25px 60px rgba(15,23,42,.08)',
                    // maxWidth: '900px',
                    maxWidth: { xs: '100%', md: '1080px', lg: '1280px' },
                    margin: '0 auto',

                }}
            >
                 {isComplete && activeStep < SUMMARY_STEP && (
                        <Box
                            display="flex"
                            mb={1}
                        >
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => setActiveStep(SUMMARY_STEP)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                ← Back to Summary
                            </Button>
                        </Box>
                    )}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeStep}
                        initial={{
                            opacity: 0,
                            y: 25,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        exit={{
                            opacity: 0,
                            y: -25,
                        }}
                        transition={{
                            duration: 0.35,
                        }}
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>
            </Paper>
            {/* {!isComplete && activeStep > 0 && activeStep < 3 && (
                <Button
                    variant="outlined"
                    onClick={() => setActiveStep(4)}
                >
                    Back to Summary
                </Button>
            )} */}
            {/* Back navigation — only during active onboarding, not in read-only review */}
            {/* {!isComplete && activeStep > 0 && activeStep < 3 && (
                <Box display="flex" justifyContent="flex-start" mt={2}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                    >
                        ← Back
                    </Button>
                </Box>
            )} */}

            {/* When revisiting a step in read-only mode, offer a way back to the summary */}
            {/* {isComplete && activeStep < 4 && (
                <Box display="flex" justifyContent="flex-start" mt={2}>
                    <Button variant="text" size="small" onClick={() => setActiveStep(4)}>
                        ← Back to Summary
                    </Button>
                </Box>
            )} */}
        </Box>
    );
};

const mapStateToProps = (state: any) => ({
    UserReducer: state.UserReducer.UserData,
    UserID: state.UserID.userID,
});

const connected = connect(mapStateToProps)(BrandAffiliateOnboardingComponent);
export { connected as BrandAffiliateOnboarding };
